<template>
  <div class="entities">
    <btn
      class="entities__basic-section-button"
      icon="save"
      @click="saveChanges"
    >{{ $t('btn.save') }}</btn>
    <section-title>{{ $t('plugins.nlp.entities.title') }}</section-title>
    <section-title subsection>{{ $t('plugins.nlp.entities.text') }}</section-title>
    <div class="entities__section editor-section">
      <div class="entities__list-container">
        <div class="entities__list-item"
          v-for="(item, index1) in entities.textEntities"
          :key="index1">
          <div class="entities__item-main-container">
            <div class="entities__item-gradient"></div>
            <div class="entities__item-main">
              <dynamic-text class="entities__list-item-name entities__input-name"
                :minWidth="80"
                :inputCustomStyle="{ 'text-align': 'left' }"
                :placeholder="$t('plugins.nlp.entities.textTable.placeholders.entity')"
                v-model="item.name"
                @changed="entityChanged(index1, $event)"/>
              <div class="entities__item-filler" @click="toggleEntriesList(index1)"></div>
              <div class="entities__item-end-cont">
                <div class="entities__item-end-start">
                  <div class="entities__item-prio-cont"
                    @click="prioChanged(item)">
                    <div :class="'entities__item-prio-dot entities__item-prio-dot' + getPrioModifierClass(item.priority)">
                    </div>
                  </div>
                  <sw-button
                    v-model="item.is_fuzzy"
                    label="fuzzy"
                    :inline="true"
                    @input="fuzzyChanged(index1)"
                  ></sw-button>
                </div>
                <fai class="entities__item-entry-del" 
                  icon="ban"
                  v-if="index1 !== entities.textEntities.length-1"
                  @click="entityDelete(index1)"
                ></fai>
              </div>
            </div>
          </div>
          <div ref="refEntry" class="entities__item-entries"
            :style="[index1 === entities.textEntities.length-1 ? { visibility: 'visible', position: 'static' } : { visibility: 'hidden', position: 'absolute' }]"
            >
            <div class="entities__item-entry"
              v-for="(entry, index2) in item.entities_entries"
              :key="index2">
              <div class="entities__item-values">
                <div class="entities__item-value">
                  <dynamic-text
                    :ref="'refValue'+index1+index2"
                    class="entities__input-value entities__input-value-ref"
                    :minWidth="40"
                    :placeholder="$t('plugins.nlp.entities.textTable.placeholders.value')"
                    :value="getInputText(getRefValue(entry.entries_values))"
                    @changed="valueChanged(item.entities_entries, entry, $event); 
                      setNextValueFocus(index1, index2, entry.entries_values.length)"/>
                </div>
                <div class="entities__item-aliases">
                  <div class="entities__item-alias"
                  v-for="(eValue, index3) in getNotRefValues(entry.entries_values)"
                  :key="index3">
                    <dynamic-text
                      :ref="'refAlias'+index1+index2+index3"
                      class="entities__input-value"
                      :minWidth="40"
                      placeholder="alias"
                      :value="getInputText(eValue)"
                      @changed="aliasChanged(entry, index3, $event);
                        setNextAliasFocus(index1, index2, index3, entry.entries_values.length)"/>
                    <fai class="entities__item-alias-del"
                      icon="ban"
                      v-if="index3 !== entry.entries_values.length-2"
                      @click="aliasDelete(entry, index3)"
                    ></fai>
                  </div>
                </div>
                <fai class="entities__item-entry-del" 
                  icon="ban"
                  v-if="index2 !== item.entities_entries.length-1"
                  @click="entryDelete(item, index2)"
                ></fai>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section-title subsection>{{ $t('plugins.nlp.entities.regex') }}</section-title>
    <div class="entities__section editor-section">
      <regex-editor 
        v-model="entities.regexEntities" 
        :toDelete="regexToSave.toDelete"
        @delChanged="updateRegexToDelete"/>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import axios from '@/axios'
import dynText from './DynamicTextarea.vue'
import regexEditor from './RegexEntities.vue'
export default {
  data () {
    return {
      entities: {
        textEntities: [],
        regexEntities: []
      },
      textToSave: {
        toDelete: { // ids
          entities: [],
          entries: [],
          values: []
        }
      },
      regexToSave: {
        toDelete: {
          entities: [],
          regexes: []
        }
      },
      textTemps: {
        eValueTemp: {
          tempStr: ""
        },
        entryTemp: {
          entries_values: [
            {
              isRefValue: true,
              tempStr: ""
            }
          ]
        },
        entityTemp: {
          name: '',
          is_fuzzy: false,
          priority: 0,
          entities_entries: [
            {
              entries_values: [
                {
                  isRefValue: true,
                  tempStr: ""
                }
              ]
            }
          ]
        }
      },
      prio: 0
    }
  },

  props: {
    value: Object
  },

  watch: {
    value: function () {
      this.entities = JSON.parse(JSON.stringify(this.value))
      this.appendNewTemplates()
    }
  },
  mounted: function () {
    this.entities = JSON.parse(JSON.stringify(this.value))
    this.appendNewTemplates()
  },

  methods: {
    //// =============================================================================
    //// getters for form components 
    getInputText (value) {
      if(value.tempStr || value.tempStr === "") return value.tempStr
      let text = ""
      for(let i = 0; i<value.values_segments.length; i++) {
        text += value.values_segments[i].ortho
      }
      return text
    },

    getRefValue (values) {
      for(let i = 0; i<values.length; i++) {
        if(values[i].isRefValue) return values[i]
      }
    },

    getNotRefValues (values) {
      return values.filter(elem => { if(!elem.isRefValue) return true })
    },

    //// =============================================================================
    //// data manipulation
    prioChanged(item) {
      if(item.priority<2) item.priority += 1
      else item.priority = 0
      item.edited = true
      this.$store.commit('SET_EDITED', true)
    },

    fuzzyChanged(index) {
      if(this.entities.textEntities[index].id) this.entities.textEntities[index].edited = true
      this.$store.commit('SET_EDITED', true)
    },

    aliasChanged (entry, index, str) {
      let eValue = entry.entries_values[index+1]
      if(!str) return 
      eValue.tempStr = str
      if (index === entry.entries_values.length-2) {
        entry.entries_values.push(JSON.parse(JSON.stringify(this.textTemps.eValueTemp)))
      }
      this.$store.commit('SET_EDITED', true)
    },

    valueChanged (entries, entry, str) {
      let eValue = entry.entries_values[0]
      if(!str) return
      eValue.tempStr = str
      if (entry.entries_values.length === 1) {
        entry.entries_values.push(JSON.parse(JSON.stringify(this.textTemps.eValueTemp)))
        entries.push(JSON.parse(JSON.stringify(this.textTemps.entryTemp)))
      }
      this.$store.commit('SET_EDITED', true)
    },

    entityChanged (index, str) {
      if(!str) return
      this.entities.textEntities[index].name = str
      if(this.entities.textEntities[index].id) this.entities.textEntities[index].edited = true
      if(index === this.entities.textEntities.length-1) {
        this.entities.textEntities.push(JSON.parse(JSON.stringify(this.textTemps.entityTemp)))
      }
      this.$store.commit('SET_EDITED', true)
    },

    appendNewTemplates () {
      for(let i = 0; i<this.entities.textEntities.length; i++) {
        for(let j = 0; j<this.entities.textEntities[i].entities_entries.length; j++) {
          this.entities.textEntities[i].entities_entries[j].entries_values.push(JSON.parse(JSON.stringify(this.textTemps.eValueTemp)))
        }
        this.entities.textEntities[i].entities_entries.push(JSON.parse(JSON.stringify(this.textTemps.entryTemp)))
      }
      this.entities.textEntities.push(JSON.parse(JSON.stringify(this.textTemps.entityTemp)))
    },

    updateRegexToDelete (toDelete) {
      this.regexToSave.toDelete = toDelete
    },

    //// =============================================================================
    //// crud to save later
    aliasDelete (entry, index) {
      let eValue = entry.entries_values[index+1]
      if(eValue.id) this.textToSave.toDelete.values.push({ id: eValue.id, parent_id: entry.id })
      entry.entries_values.splice(index+1, 1)
      this.$store.commit('SET_EDITED', true)
    },

    entryDelete (entity, index) {
      let entry = entity.entities_entries[index]
      if(entry.id) {
        // remove previously deleted children from array
        // as deleting parent will take care of them
        for(let i = 0; i<this.textToSave.toDelete.values.length; i++) {
          if(this.textToSave.toDelete.values[i].parent_id === entry.id) {
            this.textToSave.toDelete.values.splice(i, 1)
            i--
          }
        }
        this.textToSave.toDelete.entries.push({ id: entry.id, parent_id: entity.id })
      }
      entity.entities_entries.splice(index, 1)
      this.$store.commit('SET_EDITED', true)
    },

    entityDelete(index) {
      let entity = this.entities.textEntities[index]
      if(entity.id) {
        // remove previously deleted children from array
        // as deleting parent will take care of them

        // values
        let i = 0
        while(i<this.textToSave.toDelete.values.length) {
          for(let j = 0; j<entity.entities_entries.length; j++) {
            if(entity.entities_entries[j].id && (this.textToSave.toDelete.values[i].parent_id === entity.entities_entries[j].id)) {
              this.textToSave.toDelete.values.splice(i, 1)
              i--
              break;
            }
          }
          i++
        }
        // entries
        for(let i = 0; i<this.textToSave.toDelete.entries.length; i++) {
          if(this.textToSave.toDelete.entries[i].parent_id === entity.id) {
            this.textToSave.toDelete.entries.splice(i, 1)
            i--
          }
        }
        this.textToSave.toDelete.entities.push(entity.id)
      }
      this.entities.textEntities.splice(index, 1)
      this.$store.commit('SET_EDITED', true)
    },

    //// =============================================================================
    //// API
    async saveChanges () {
      try {
        // text entities
        let copyTextEntities = JSON.parse(JSON.stringify(this.entities.textEntities))
        this.clearTemplates(copyTextEntities)
        let textToSend = await this.createTextEditObject(copyTextEntities)
        // regex entities
        let copyRegexEntities = JSON.parse(JSON.stringify(this.entities.regexEntities))
        let regexToSend = this.createRegexEditObject(copyRegexEntities)
        let res = await axios.patch('/api/plugin/nlp/entities/update', {
          textEntities: textToSend,
          regexEntities: regexToSend
        })
        const textIds = res.data.textEntitiesIds
        const regexIds = res.data.regexEntitiesIds
        // text entities
        this.cleanAndFillTextWithIds(copyTextEntities, textIds)
        // regex entities
        this.fillCreatedRegexEntitiesWithIds(regexToSend.entities.toCreate, regexIds.entitiesIds.createdIds)
        this.fillCreatedRegexValuesWithIds(regexToSend.regexes.toCreate, regexIds.regexesIds.createdIds)

        this.entities.textEntities = copyTextEntities
        this.entities.regexEntities = copyRegexEntities
        this.$emit('input', this.entities)
        this.notify('success', 'plugins.nlp.success.entities.edit')
        this.$store.commit('SET_EDITED', false)
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.entities.edit')
        console.error(e)
      }
    },

    async createTextEditObject (textEntities) {
      try {
        // text entities
        let textToSave = {
          entities: {
            toCreate: [],
            toEdit: [],
            toDelete: this.textToSave.toDelete.entities
          },
          entries: {
            toCreate: [],
            toDelete: this.textToSave.toDelete.entries.map(item => { return item.id })
          },
          values: {
            toCreate: [],
            toEdit: [],
            toDelete: this.textToSave.toDelete.values.map(item => { return item.id })
          }
        }
        // entities
        for(let i = 0; i<textEntities.length; i++) {
          let actEntity = textEntities[i]
          if(actEntity.id === undefined) {
            textToSave.entities.toCreate.push(actEntity)
          } else {
            if(actEntity.edited) {
              textToSave.entities.toEdit.push({
                id: actEntity.id,
                name: actEntity.name,
                is_fuzzy: actEntity.is_fuzzy,
                priority: actEntity.priority
              })
            }
            // entries
            for(let j = 0; j<actEntity.entities_entries.length; j++) {
              let actEntry = actEntity.entities_entries[j]
              if(actEntry.id === undefined) {
                textToSave.entries.toCreate.push(actEntry)
                actEntry.entities_id = actEntity.id
              } else {
                // values
                for(let k = 0; k<actEntry.entries_values.length; k++) {
                  let actValue = actEntry.entries_values[k]
                  if(actValue.id === undefined) {
                    textToSave.values.toCreate.push(actValue)
                    actValue.entities_entries_id = actEntry.id
                  } else {
                    if(actValue.tempStr) {
                      textToSave.values.toEdit.push(actValue)
                    }
                  }
                }
              }
            }
          }
        }
        
        let valuesToSegmentize = this.findAllValuesForSegmentation(textToSave)
        let segmentized = await axios.post('/api/plugin/nlp/segmentize', {
          texts: valuesToSegmentize.map(item => { return item.tempStr })
        })
        
        for(let i = 0; i<valuesToSegmentize.length; i++) {
          valuesToSegmentize[i].values_segments = segmentized.data.segments[i]
          delete valuesToSegmentize[i].tempStr
        }
        return textToSave
      } catch(e) {
        return e
      }
    },

    createRegexEditObject (regexEntities) {
      // regex entities
      let regexToSave = {
        entities: {
          toCreate: [],
          toEdit: [],
          toDelete: this.regexToSave.toDelete.entities
        },
        regexes: {
          toCreate: [],
          toEdit: [],
          toDelete: this.regexToSave.toDelete.regexes.map(item => { return item.id })
        }
      }
  
      for(let i = 0; i<regexEntities.length; i++) {
        let actEntity = regexEntities[i]
        if(actEntity.id === undefined) {
          regexToSave.entities.toCreate.push(actEntity)
        } else {
          if(actEntity.edited) {
            regexToSave.entities.toEdit.push({
              id: actEntity.id,
              name: actEntity.name,
              priority: actEntity.priority
            })
          }
          for(let j = 0; j<actEntity.entities_regexes.length; j++) {
            let regex = actEntity.entities_regexes[j]
            if(regex.id === undefined) {
              regexToSave.regexes.toCreate.push(regex)
              regex.entities_id = actEntity.id
            } else {
              if(regex.edited) {
                regexToSave.regexes.toEdit.push({
                  id: regex.id,
                  exp: regex.exp
                })
              }
            }
          }
        }
      }
      return regexToSave
    },

    findAllValuesForSegmentation (textToSave) {
      let values = []
      for(let i = 0; i<textToSave.entities.toCreate.length; i++) {
        let actEntity = textToSave.entities.toCreate[i]
        for(let j = 0; j<actEntity.entities_entries.length; j++) {
          let actEntry = actEntity.entities_entries[j]
          for(let k = 0; k<actEntry.entries_values.length; k++) {
            values.push(actEntry.entries_values[k])
          }
        }
      }
      for(let i = 0; i<textToSave.entries.toCreate.length; i++) {
        let actEntry = textToSave.entries.toCreate[i]
        for(let j = 0; j<actEntry.entries_values.length; j++) {
          values.push(actEntry.entries_values[j])
        }
      }
      return values.concat(textToSave.values.toCreate.concat(textToSave.values.toEdit))
    },

    clearTemplates (entities) {
      for(let i = 0; i<entities.length; i++) {
        let actEntity = entities[0]
        for(let j = 0; j<entities[i].entities_entries.length; j++) {
          entities[i].entities_entries[j].entries_values.pop()
        }
        entities[i].entities_entries.pop()
      }
      entities.pop()
    },

    cleanAndFillTextWithIds (textEntities, textIds) {
      for(let i = 0; i<textEntities.length; i++) {
        let actEntity = textEntities[i]
        if(actEntity.id === undefined) {
          this.fillCreatedTextEntityWithIds(actEntity, textIds.entitiesIds.createdIds.shift())
          continue
        }
        for(let j = 0; j<actEntity.entities_entries.length; j++) {
          let actEntry = actEntity.entities_entries[j]
          if(actEntry.id === undefined) {
            this.fillCreatedEntryWithIds(actEntry, textIds.entriesIds.createdIds.shift())
            continue
          }
          for(let k = 0; k<actEntry.entries_values.length; k++) {
            let actValue = actEntry.entries_values[k]
            if(actValue.id === undefined) {
              actValue.id = textIds.valuesIds.createdIds.shift().id
            }
          }
        }
      }
    },

    fillCreatedTextEntityWithIds (entity, idsTree) {
      entity.id = idsTree.id
      for(let i = 0; i<entity.entities_entries.length; i++) {
        let actEntry = entity.entities_entries[i]
        actEntry.id = idsTree.entriesIds[i].id
        for(let j = 0; j<actEntry.entries_values.length; j++) {
          let actValue = actEntry.entries_values[j]
          actValue.id = idsTree.entriesIds[i].valuesIds[j].id
        }
      }
    },

    fillCreatedEntryWithIds (entry, idsTree) {
      entry.id = idsTree.id
      for(let i = 0; i<entry.entries_values.length; i++) {
        let actValue = entry.entries_values[i]
        actValue.id = idsTree.valuesIds[i].id
      }
    },

    fillCreatedRegexEntitiesWithIds (createdRegexEntities, idsTree) {
      for(let i = 0; i<createdRegexEntities.length; i++) {
        const actRegexEntity = createdRegexEntities[i]
        actRegexEntity.id = idsTree[i].id
        this.fillCreatedRegexValuesWithIds(actRegexEntity.entities_regexes, idsTree[i].regexesIds)
      }
    },

    fillCreatedRegexValuesWithIds (createdRegexValues, keys) {
      for(let i = 0; i<createdRegexValues.length; i++) {
        createdRegexValues[i].id = keys[i]
      }
    },

    //// =============================================================================
    //// visibility, misc
    setNextValueFocus (index1, index2, len) {
      if(len>2) return
      setTimeout(() => {
        //this.$refs['refValue'+index1+''+(index2+1)][0].$el.children[0].focus()
        this.$refs['refAlias'+index1+''+index2+''+0][0].$el.children[0].focus()
      }, 0)
    },

    setNextAliasFocus (index1, index2, index3, len) {
      if(index3<len-3) return
      setTimeout(() => {
        this.$refs['refAlias'+index1+''+index2+''+(index3+1)][0].$el.children[0].focus()
      }, 0)
    },

    toggleEntriesList (index) {
      let elem = this.$refs.refEntry[index]
      if(elem.style.visibility === 'hidden') {
        elem.style.visibility = 'visible'
        elem.style.position = 'static'
      }
      else {
        elem.style.visibility = 'hidden'
        elem.style.position = 'absolute'
      }
    },

    getPrioModifierClass (value) {
      if(value === 0) return ''
      else if(value === 1) return '--yellow'
      return '--red'
    }
  },

  components: {
    'dynamic-text': dynText,
    'regex-editor': regexEditor
  }
}
</script>

<style lang="scss">
.entities {
  padding-left: 25px;
  padding-top: 6px;

  &__section {
    margin-bottom: 20px;
    width: 50%;
    min-width: 540px;
    position: relative;
  }

  &__list-item {
    margin-bottom: 10px;
  }

  &__item-main-container {
    display: flex;
    width: 100%;
    margin-bottom: 4px;
  }

  &__item-main {
    background-color: white;
    border-radius: 0px 4px 4px 0px;
    padding: 8px 10px;
    display: flex;
    align-items: center;
    flex-grow: 1;
  }

  &__item-gradient {
    background-color: $primary;
    border-radius: 4px 0px 0px 4px;
    width: 4px;
    position: relative;
  }

  &__item-filler {
    cursor: pointer;
    flex-grow: 1;
    height: 100%;
  }

  &__input-name {
    font-size: inherit;
    font-family: inherit;
  }

  &__input-name>input:placeholder-shown {
  }

  &__item-entry {
    width: 90%;
    background: #f7f7f7;
    padding: 6px;
    margin-bottom: 4px;
    border-radius: 4px;
    display: flex;
    margin-left: 4px;
  }

  &__item-values {
    display: flex;
    align-items: flex-start;
    flex-grow: 1;
  }

  &__item-value {
    min-width: 25%;
    display: flex;
    margin-right: 5px;
  }

  &__input-value {
    font-size: 0.8em;
  }

  &__input-value-ref {
    padding: 4px;
    border: solid 1px;
    border-color: chocolate;
    background: white;
    border-radius: 4px;
  }

  &__item-aliases {
    display: flex;
    flex-wrap: wrap;
    margin: -4px;
    flex-grow: 1;
  }

  &__item-alias {
    padding: 4px;
    border: solid 1px;
    border-radius: 4px;
    border-color: lightgrey;
    background: white;
    margin-right: 4px;
    display: flex;
    align-items: center;
    margin: 4px;
  }

  &__item-alias-rows {
    margin-top: 4px;
  }

  &__item-alias-del {
    cursor: pointer;
    padding: 4px;
    font-size: 0.65em;
  }

  &__item-end-cont {
    min-width: 40%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__item-end-start {
    display: flex;
  }

  &__item-entry-del {
    cursor: pointer;
    padding: 4px;
    align-self: center;
  }

  &__basic-section-button {
    position: absolute;
    right: 0;
    margin-right: 10px;
    margin-top: 10px;
  }

  &__item-prio-cont {
    padding: 4px;
    margin-right: 16px;
  }

  &__item-prio-dot {
    cursor: pointer;
    background: #62A87C;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    transition-property: background-color;
    transition-duration: 0.3s;
    border: solid 1px;
    border-color: #e6e6e6;

    &--yellow {
      background: #FFFC99;
    }

    &--red {
      background: #E28565;
    }
  }
}
</style>